import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Card,
    CardContent,
    Button,
    IconButton,
    TextField,
    Stack,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    Select,
    MenuItem,
    Divider,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ShareIcon from '@mui/icons-material/Share';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { PageHeaderCard } from '../../components/common';
import { useNotification } from '../../contexts/NotificationContext';
import {
    useShare,
    LINK_EXPIRATION,
    LINK_EXPIRATION_LABELS,
} from '../../contexts/ShareContext';
import { weeklyReportsAPI } from '../../services/api';
import { formatDate } from '../../utils/helper';

const DAY_OPTIONS = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' },
];

const createEmptyDailyEntries = () => (
    DAY_OPTIONS.reduce((acc, day) => {
        acc[day.key] = [{ project: '', description: '' }];
        return acc;
    }, {})
);

const toLocalDateString = (value) => {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const getMondaySundayRange = (weekOffset = 0) => {
    const now = new Date();
    now.setDate(now.getDate() + (weekOffset * 7));

    const monday = new Date(now);
    const day = monday.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    monday.setDate(monday.getDate() + diff);
    monday.setHours(12, 0, 0, 0);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(12, 0, 0, 0);

    return {
        startDate: monday,
        endDate: sunday,
        start: formatDate(monday),
        end: formatDate(sunday),
    };
};

const toMonday = (value) => {
    const base = new Date(value);
    if (Number.isNaN(base.getTime())) {
        return null;
    }
    const normalized = new Date(base);
    const day = normalized.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    normalized.setDate(normalized.getDate() + diff);
    normalized.setHours(12, 0, 0, 0);
    return normalized;
};

const calculateWeekOffset = (weekStartValue) => {
    if (!weekStartValue) {
        return null;
    }
    const currentMonday = toMonday(new Date());
    const targetMonday = toMonday(weekStartValue);
    if (!currentMonday || !targetMonday) {
        return null;
    }

    const diffMs = targetMonday.getTime() - currentMonday.getTime();
    return Math.round(diffMs / (7 * 24 * 60 * 60 * 1000));
};

const normalizeDailyEntriesFromApi = (entries = []) => {
    const next = createEmptyDailyEntries();

    DAY_OPTIONS.forEach((day) => {
        const dayEntries = entries
            .filter((entry) => (entry.dayOfWeek || entry.day_of_week) === day.key)
            .map((entry) => ({
                project: entry.project || entry.task || '',
                description: entry.description || '',
            }));

        next[day.key] = dayEntries.length > 0 ? dayEntries : [{ project: '', description: '' }];
    });

    return next;
};

const WeeklyReports = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { showSuccess, showError, showWarning } = useNotification();
    const { generateShareLink } = useShare();
    const [searchParams] = useSearchParams();

    const [weekOffset, setWeekOffset] = useState(0);
    const [entriesByDay, setEntriesByDay] = useState(createEmptyDailyEntries());
    const [reportTitle, setReportTitle] = useState('');
    const [reportId, setReportId] = useState(null);
    const [isPublished, setIsPublished] = useState(false);
    const [publishedAt, setPublishedAt] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [shareExpiration, setShareExpiration] = useState(LINK_EXPIRATION.ONE_WEEK);
    const [generatedLink, setGeneratedLink] = useState('');

    const weekRange = useMemo(() => getMondaySundayRange(weekOffset), [weekOffset]);

    useEffect(() => {
        const selectedWeekStart = searchParams.get('weekStart');
        const offset = calculateWeekOffset(selectedWeekStart);
        if (offset === null) {
            return;
        }
        setWeekOffset(offset);
    }, [searchParams]);

    useEffect(() => {
        let isMounted = true;

        const loadReport = async () => {
            setIsLoading(true);
            try {
                const report = await weeklyReportsAPI.getMineByWeek(toLocalDateString(weekRange.startDate));
                if (!isMounted) return;

                if (report) {
                    setReportId(report.id);
                    setReportTitle(report.title || '');
                    setIsPublished(Boolean(report.isPublished));
                    setPublishedAt(report.publishedAt || null);
                    setEntriesByDay(normalizeDailyEntriesFromApi(report.entries || []));
                } else {
                    setReportId(null);
                    setReportTitle('');
                    setIsPublished(false);
                    setPublishedAt(null);
                    setEntriesByDay(createEmptyDailyEntries());
                }
            } catch {
                if (!isMounted) return;
                setReportId(null);
                setReportTitle('');
                setIsPublished(false);
                setPublishedAt(null);
                setEntriesByDay(createEmptyDailyEntries());
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadReport();

        return () => {
            isMounted = false;
        };
    }, [weekRange.startDate]);

    const handleEntryChange = (dayKey, index, field, value) => {
        setEntriesByDay((prev) => {
            const next = { ...prev };
            const rows = [...(next[dayKey] || [])];
            rows[index] = {
                ...rows[index],
                [field]: value,
            };
            next[dayKey] = rows;
            return next;
        });
    };

    const handleAddEntry = (dayKey) => {
        setEntriesByDay((prev) => ({
            ...prev,
            [dayKey]: [...(prev[dayKey] || []), { project: '', description: '' }],
        }));
    };

    const handleRemoveEntry = (dayKey, index) => {
        setEntriesByDay((prev) => {
            const rows = [...(prev[dayKey] || [])];
            rows.splice(index, 1);
            return {
                ...prev,
                [dayKey]: rows.length > 0 ? rows : [{ project: '', description: '' }],
            };
        });
    };

    const buildPayload = () => {
        const entries = [];

        DAY_OPTIONS.forEach((day) => {
            const rows = entriesByDay[day.key] || [];
            rows.forEach((row, index) => {
                const project = (row.project || '').trim();
                const description = (row.description || '').trim();
                if (!project && !description) return;
                if (!project) return;

                entries.push({
                    dayOfWeek: day.key,
                    project,
                    description: description || null,
                    sortOrder: index,
                });
            });
        });

        return {
            weekStart: toLocalDateString(weekRange.startDate),
            title: reportTitle.trim() || null,
            entries,
        };
    };

    const saveReport = async () => {
        const payload = buildPayload();
        if (payload.entries.length === 0) {
            showWarning('Please add at least one project entry before saving.');
            return null;
        }

        setIsSaving(true);
        try {
            const saved = await weeklyReportsAPI.upsert(payload);
            setReportId(saved.id);
            setIsPublished(Boolean(saved.isPublished));
            setPublishedAt(saved.publishedAt || null);
            showSuccess('Weekly report saved successfully.');
            return saved;
        } catch (error) {
            showError(error?.message || 'Failed to save weekly report');
            return null;
        } finally {
            setIsSaving(false);
        }
    };

    const handlePublish = async () => {
        const publishReportId = await ensureSavedReportId();
        if (!publishReportId) {
            return;
        }

        setIsPublishing(true);
        try {
            const published = await weeklyReportsAPI.publish(publishReportId);
            setReportId(published.id);
            setIsPublished(Boolean(published.isPublished));
            setPublishedAt(published.publishedAt || null);
            showSuccess('Weekly report published successfully.');
        } catch (error) {
            showError(error?.message || 'Failed to publish weekly report');
        } finally {
            setIsPublishing(false);
        }
    };

    const ensureSavedReportId = async () => {
        if (reportId) {
            return reportId;
        }

        const saved = await saveReport();
        return saved?.id || null;
    };

    const handleExport = async (format) => {
        const exportReportId = await ensureSavedReportId();
        if (!exportReportId) {
            return;
        }

        try {
            const { blob, filename } = await weeklyReportsAPI.exportById({ reportId: exportReportId, format });
            const objectUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(objectUrl);
        } catch (error) {
            showError(error?.message || 'Failed to export weekly report');
        }
    };

    const handleOpenShareDialog = () => {
        setGeneratedLink('');
        setShareExpiration(LINK_EXPIRATION.ONE_WEEK);
        setShareDialogOpen(true);
    };

    const handleCloseShareDialog = () => {
        setShareDialogOpen(false);
        setGeneratedLink('');
    };

    const handleGenerateShareLink = async () => {
        const shareReportId = await ensureSavedReportId();
        if (!shareReportId) {
            return;
        }

        try {
            const link = await generateShareLink({
                reportType: 'weekly',
                weeklyReportId: shareReportId,
                expirationDays: Number(shareExpiration),
            });
            setGeneratedLink(link);
        } catch (error) {
            showError(error?.message || 'Failed to generate share link');
        }
    };

    const handleCopyLink = async () => {
        if (!generatedLink) return;
        try {
            await navigator.clipboard.writeText(generatedLink);
            showSuccess('Share link copied to clipboard.');
        } catch {
            showError('Failed to copy share link');
        }
    };

    return (
        <Box>
            <PageHeaderCard
                mb={{ xs: 2, sm: 4 }}
                title="Weekly Reports"
                subtitle={`${weekRange.start} - ${weekRange.end}`}
                titleVariant={isMobile ? 'h5' : 'h4'}
                icon={<CalendarTodayIcon sx={{ color: 'primary.main', fontSize: { xs: 22, sm: 26 } }} />}
                actions={(
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Previous Week">
                            <IconButton onClick={() => setWeekOffset((prev) => prev - 1)} size="small">
                                <ChevronLeftIcon />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="outlined"
                            size={isMobile ? 'small' : 'medium'}
                            onClick={() => setWeekOffset(0)}
                            disabled={weekOffset === 0}
                        >
                            {isMobile ? 'Current' : 'Current Week'}
                        </Button>
                        <Tooltip title="Next Week">
                            <span>
                                <IconButton
                                    onClick={() => setWeekOffset((prev) => prev + 1)}
                                    size="small"
                                    disabled={weekOffset >= 0}
                                >
                                    <ChevronRightIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                )}
            />

            <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        label="Report Title (Optional)"
                        placeholder="Weekly Activity Report"
                        value={reportTitle}
                        onChange={(event) => setReportTitle(event.target.value)}
                    />
                    {isPublished && (
                        <Box sx={{ minWidth: { xs: '100%', md: 280 }, display: 'flex', alignItems: 'center' }}>
                            <Button
                                fullWidth
                                sx={{ height: '100%' }}
                                variant="outlined"
                                color="success"
                                startIcon={<CheckCircleIcon />}
                                disabled
                            >
                                Published{publishedAt ? ` (${formatDate(publishedAt)})` : ''}
                            </Button>
                        </Box>
                    )}
                </Stack>

                {isLoading ? (
                    <Typography variant="body2" color="text.secondary">Loading report for selected week...</Typography>
                ) : (
                    <Stack spacing={2}>
                        {DAY_OPTIONS.map((day) => (
                            <Card key={day.key} variant="outlined">
                                <CardContent>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            mb: 1,
                                            gap: 1,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <Typography variant="h6">{day.label}</Typography>
                                    </Box>
                                    <Stack spacing={1.5}>
                                        {(entriesByDay[day.key] || []).map((row, index) => (
                                            <Stack
                                                key={`${day.key}-${index}`}
                                                direction={{ xs: 'column', md: 'row' }}
                                                spacing={1}
                                                alignItems={{ xs: 'stretch', md: 'flex-start' }}
                                            >
                                                <TextField
                                                    label="Project"
                                                    value={row.project}
                                                    onChange={(event) => handleEntryChange(day.key, index, 'project', event.target.value)}
                                                    sx={{ width: { xs: '100%', md: 320 } }}
                                                />
                                                <TextField
                                                    label="Description"
                                                    value={row.description}
                                                    onChange={(event) => handleEntryChange(day.key, index, 'description', event.target.value)}
                                                    multiline
                                                    minRows={1}
                                                    maxRows={4}
                                                    fullWidth
                                                    InputLabelProps={{
                                                        sx: {
                                                            bgcolor: 'background.paper',
                                                            px: 0.5,
                                                        },
                                                    }}
                                                />
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, alignSelf: { xs: 'flex-end', md: 'center' } }}>
                                                    <Tooltip title="Add Project">
                                                        <span>
                                                            <IconButton
                                                                onClick={() => handleAddEntry(day.key)}
                                                                disabled={isLoading || isSaving}
                                                                aria-label={`Add project for ${day.label}`}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: 'primary.main',
                                                                    color: 'primary.contrastText',
                                                                    '&:hover': {
                                                                        bgcolor: 'primary.dark',
                                                                    },
                                                                    '&.Mui-disabled': {
                                                                        bgcolor: 'action.disabledBackground',
                                                                        color: 'action.disabled',
                                                                    },
                                                                }}
                                                            >
                                                                <AddIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleRemoveEntry(day.key, index)}
                                                        aria-label="Remove task row"
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Stack direction="row" spacing={1}>
                        <Button variant="contained" onClick={saveReport} disabled={isSaving || isLoading}>
                            {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                        <Button
                            variant="outlined"
                            color="success"
                            onClick={handlePublish}
                            disabled={isLoading || isSaving || isPublishing || isPublished}
                        >
                            {isPublished ? 'Published' : (isPublishing ? 'Publishing...' : 'Publish')}
                        </Button>
                        <Button variant="outlined" onClick={() => handleExport('pdf')} disabled={isLoading || isSaving}>
                            Export PDF
                        </Button>
                        <Button variant="outlined" onClick={() => handleExport('excel')} disabled={isLoading || isSaving}>
                            Export Excel
                        </Button>
                        <Button variant="outlined" onClick={handleOpenShareDialog} disabled={isLoading || isSaving}>
                            Share
                        </Button>
                    </Stack>
                </Box>
            </Paper>

            <Dialog
                open={shareDialogOpen}
                onClose={handleCloseShareDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ bgcolor: 'primary.main', color: 'secondary.contrastText' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ShareIcon color="inherit" />
                        <Typography variant="h6" fontWeight={600} color="inherit">Share Report</Typography>
                    </Box>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Box sx={{ py: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Link Expiration
                        </Typography>
                        <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                            <Select
                                value={shareExpiration}
                                onChange={(event) => setShareExpiration(Number(event.target.value))}
                            >
                                {Object.entries(LINK_EXPIRATION_LABELS).map(([key, label]) => (
                                    <MenuItem key={key} value={Number(key)}>{label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {!generatedLink ? (
                            <Button
                                variant="contained"
                                fullWidth
                                startIcon={<LinkIcon />}
                                onClick={handleGenerateShareLink}
                            >
                                Generate Share Link
                            </Button>
                        ) : (
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Share Link
                                </Typography>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        bgcolor: 'grey.50',
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={generatedLink}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: 'background.paper',
                                            },
                                        }}
                                    />
                                    <Tooltip title="Copy to clipboard">
                                        <IconButton color="primary" onClick={handleCopyLink}>
                                            <ContentCopyIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Paper>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseShareDialog} variant="outlined">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WeeklyReports;
